import { css } from '@emotion/react';
import {
  millisecondsToSeconds, useAnimate,
} from 'motion/react';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';

const px = (px: number): string => `${px}px`;

const staticHighlightStyles = (theme: Theme) => css({
  position: 'relative',
  ':after': {
    position: 'absolute',
    content: '" "',
    inset: 0,
    boxShadow: `inset 0 0 var(--box-shadow-blur) var(--box-shadow-size) ${theme.borderColors.activeItem}`,
    pointerEvents: 'none',
  },
});

const initialDynamicStyles = {
  '--box-shadow-size': '0px',
  '--box-shadow-blur': '0px',
} as const;

type HighlightAnimationOptions = {
  duration: number;
  pulseDuration: number;
  size: number;
  blur: number;
};

const defaultOptions: HighlightAnimationOptions = {
  duration: 900,
  pulseDuration: 500,
  size: 0,
  blur: 24,
};

export const useHighlightAnimation = ({
  duration = defaultOptions.duration,
  pulseDuration = defaultOptions.pulseDuration,
  size = defaultOptions.size,
  blur = defaultOptions.blur,
}: Partial<HighlightAnimationOptions> = {}) => {
  const [animateHighlight, setAnimateHighlight] = useState(false);
  const [scope, animate] = useAnimate();
  const durationRef = useRef(duration);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  useEffect(() => {
    if (!animateHighlight) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      const end = Date.now() + durationRef.current;
      const singlePulseDuration = millisecondsToSeconds(pulseDuration / 2); // Adjust the duration for speed control

      while (Date.now() < end && !cancelled) {
        await animate(scope.current, {
          '--box-shadow-size': px(size),
          '--box-shadow-blur': px(blur),
        }, { duration: singlePulseDuration });

        await animate(scope.current, initialDynamicStyles, { duration: singlePulseDuration });
      }

      if (!cancelled) {
        await animate(scope.current, initialDynamicStyles, { duration: 0.1 });
        setAnimateHighlight(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [animateHighlight, animate, blur, pulseDuration, scope, size]);

  const startHighlightAnimation = useCallback(() => setAnimateHighlight(true), []);

  return {
    staticHighlightStyles,
    startHighlightAnimation,
    animationScopeRef: scope,
    isAnimationActive: animateHighlight,
  };
};
